import { TreeItems } from 'dnd-kit-sortable-tree';
import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { validateLang } from '../../../../../../helpers/lang';
import { concatPath } from '../../../../../../helpers/path';
import { validate } from '../../../../../../helpers/validate';
import { useLang } from '../../../../../../hooks/use-lang';
import { useModalParams } from '../../../../../../hooks/use-modal-params';
import { ManagerPath, RootPath, TreesPath } from '../../../../../../routes/manager/manager-enums';
import { TreeNodeType } from '../../../../../../store/api/trees-api';
import { useTreeStore } from '../../../../../../store/use-tree-store';
import { TreeStructureNodeProps } from './tree-structure-node/tree-structure-node';

export const useTreeStructure = () => {
  const { tree, handleUpdateTree, postTree, putTree, isLoading } = useTreeStore();

  const [params] = useSearchParams();
  const id = params.get('id');

  const { t } = useLang();

  const nodes = useMemo(() => {
    const getNodes = (nodes: Partial<TreeNodeType>[]): TreeItems<TreeStructureNodeProps> => {
      return nodes.map((node) => ({
        id: node.id || Math.random(),
        node,
        children: getNodes(node.children || []),
        collapsed: node.collapsed,
      }));
    };
    return getNodes(tree?.nodes || []);
  }, [tree?.nodes]);

  const updateTree = (items: TreeItems<TreeStructureNodeProps>) => {
    const updateNodes = (nodes: TreeItems<TreeStructureNodeProps>): Partial<TreeNodeType>[] => {
      return nodes.map((node) => ({
        ...node.node,
        children: updateNodes(node.children || []),
        id: Number(node.id),
        collapsed: node.collapsed,
      }));
    };
    const result = updateNodes(items);
    handleUpdateTree({ nodes: result });
  };

  const navigate = useNavigate();

  const handleSave = async () => {
    const isValid = validate({ name: { rule: () => validateLang(tree?.name) } });
    if (!isValid) return;

    try {
      if (id) {
        await putTree();
        window.history.back();
      } else {
        await postTree();
        navigate(concatPath([RootPath.Manager, ManagerPath.Trees, TreesPath.ListTree]));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const { handleCloseModal, isOpen } = useModalParams('deleteNode');

  return {
    nodes,
    updateTree,
    handleSave,
    isLoading,
    isOpen,
    handleCloseModal,
    t,
  };
};
